import { Moment } from 'moment';
import { formatDate } from '@shared/utils/date';
import { getReceivableLabel } from '@shared/utils/receivablesHelpers';
import { DashboardPeriods, DaysDashboardPeriods, NumberFormatStyle } from '@shared/utils/constants';
import type { PayablesAgingItem } from '@api/features/statistics/getPayablesAgingData';

type DashboardRow = {
	counterparty: string | null;
	notDuePayable: number;
	overflowPayable: number;
	total: number;
	payable: {
		range: [Moment, Moment];
		label: string;
		payable: number;
	}[];
};

const formatCsvNumber = (value: number, formatStyle: NumberFormatStyle) => {
	if (!Number.isFinite(value)) {
		return '';
	}

	if (formatStyle === NumberFormatStyle.UkUs) {
		return String(value.toFixed(2));
	}

	return String(value.toFixed(2).replace('.', ','));
};

export const buildPayablesAgingCsvData = ({
	title,
	daysPeriod,
	ranges,
	formatStyle,
	summaryRows,
	detailRows,
}: {
	title: string;
	daysPeriod: DaysDashboardPeriods;
	ranges: Array<[Moment, Moment]>;
	formatStyle: NumberFormatStyle;
	summaryRows: DashboardRow[];
	detailRows: PayablesAgingItem[];
}) => {
	const agedHeaders = [
		'Counterparty',
		'Not Due',
		...ranges.map((_r, i) => getReceivableLabel(i, daysPeriod)),
		daysPeriod === DashboardPeriods.SIXTY_DAYS ? '+60 days' : '+120 days',
		'Total',
	];

	const agedRows = (() => {
		const base = summaryRows.map((cp) => ([
			cp.counterparty ?? '',
			formatCsvNumber(cp.notDuePayable ?? 0, formatStyle),
			...ranges.map((_r, i) => formatCsvNumber(cp.payable?.[i]?.payable ?? 0, formatStyle)),
			formatCsvNumber(cp.overflowPayable ?? 0, formatStyle),
			formatCsvNumber(cp.total ?? 0, formatStyle),
		]));

		const totalNotDue = summaryRows.reduce((t, cp) => t + (cp.notDuePayable ?? 0), 0);
		const totalOverflow = summaryRows.reduce((t, cp) => t + (cp.overflowPayable ?? 0), 0);
		const totalsByRange = ranges.map((_r, i) =>
			summaryRows.reduce((t, cp) => t + (cp.payable?.[i]?.payable ?? 0), 0),
		);
		const grandTotal = summaryRows.reduce((t, cp) => t + (cp.total ?? 0), 0);

		const totalRow = ([
			'Total',
			formatCsvNumber(totalNotDue, formatStyle),
			...totalsByRange.map((v) => formatCsvNumber(v, formatStyle)),
			formatCsvNumber(totalOverflow, formatStyle),
			formatCsvNumber(grandTotal, formatStyle),
		]);

		return [...base, totalRow];
	})();

	const detailHeaders = [
		'Vessel',
		'Contract',
		'Contract type',
		'Counterparty',
		'Description',
		'Invoice date',
		'Due date',
		'Days outstanding',
		'Invoiced',
		'Paid',
		'Outstanding',
		'Currency',
	];

	const detailRowsFormatted = detailRows.map((r) => ([
		r.vessel?.name ?? '',
		r.contractIdentifier ?? '',
		r.contractType ? String(r.contractType) : '',
		r.counterparty?.name ?? '',
		r.description ?? '',
		r.invoiceDate ? formatDate(r.invoiceDate) : '',
		r.dueDate ? formatDate(r.dueDate) : '',
		r.daysOutstanding ?? '',
		formatCsvNumber(r.amount ?? 0, formatStyle),
		formatCsvNumber(r.paid ?? 0, formatStyle),
		formatCsvNumber(r.outstanding ?? 0, formatStyle),
		r.currency ?? '',
	]));

	return [
		[title],
		agedHeaders,
		...agedRows,
		[],
		[],
		['Payables (filtered + sorted)'],
		detailHeaders,
		...detailRowsFormatted,
	];
};

